/**
 * delete-cascade.ts — orphan checker: khi xoá một entity, dọn mọi tham chiếu
 * chết trên các collection khác để không còn link "ma" (reliability wave).
 *
 * Pure module: chỉ biến đổi các collection và TRẢ VỀ danh sách item bị sửa
 * (`dirtied`) + danh sách task cần xoá tiếp (`removeTaskIds`). Caller
 * (lifeOsMutations.removeCollectionItem) chịu trách nhiệm markDirty/markDeleted
 * — tách như vậy để tránh vòng import với lifeOsMutations.ts.
 *
 * Phạm vi cascade theo collection bị xoá:
 *   goal     → tasks.goalId, projects.goalId, habits.linkedGoalId, notes.linkedGoalId
 *   project  → tasks.projectId, notes.linkedProjectId
 *   task     → events.linkedTaskId, dailyPlans.mitTaskIds,
 *              instance định kỳ: xoá instance TƯƠNG LAI chưa xong (dừng chuỗi),
 *              instance quá khứ/đã xong chỉ gỡ recurrenceParentId/Key (giữ lịch sử)
 *   account  → transactions.accountId/toAccountId, recurring.accountId
 *   (note/habit/budget/event/… không có collection nào tham chiếu ngược → no-op)
 */
import type { LifeOsCollectionName, LifeOsState } from "../types";
import { todayIso } from "./date";

export type CascadeRef = { collection: LifeOsCollectionName; id: string };

export type CascadeResult = {
  state: LifeOsState;
  /** Items đã bị sửa link — caller phải markDirty từng cái để đẩy lên cloud. */
  dirtied: CascadeRef[];
  /** Task ids cần xoá tiếp (instance tương lai của series parent vừa xoá). */
  removeTaskIds: string[];
};

export function cascadeDanglingRefs(
  state: LifeOsState,
  collection: LifeOsCollectionName,
  deletedId: string,
  today = todayIso(),
): CascadeResult {
  let next = state;
  const dirtied: CascadeRef[] = [];
  const removeTaskIds: string[] = [];

  if (collection === "goals") {
    const tasks = next.tasks.filter((t) => t.goalId === deletedId);
    if (tasks.length) {
      next = { ...next, tasks: next.tasks.map((t) => (t.goalId === deletedId ? { ...t, goalId: undefined } : t)) };
      tasks.forEach((t) => dirtied.push({ collection: "tasks", id: t.id }));
    }
    const projects = next.projects.filter((p) => p.goalId === deletedId);
    if (projects.length) {
      next = { ...next, projects: next.projects.map((p) => (p.goalId === deletedId ? { ...p, goalId: undefined } : p)) };
      projects.forEach((p) => dirtied.push({ collection: "projects", id: p.id }));
    }
    const habits = next.habits.filter((h) => h.linkedGoalId === deletedId);
    if (habits.length) {
      next = { ...next, habits: next.habits.map((h) => (h.linkedGoalId === deletedId ? { ...h, linkedGoalId: undefined } : h)) };
      habits.forEach((h) => dirtied.push({ collection: "habits", id: h.id }));
    }
    const notes = next.notes.filter((n) => n.linkedGoalId === deletedId);
    if (notes.length) {
      next = { ...next, notes: next.notes.map((n) => (n.linkedGoalId === deletedId ? { ...n, linkedGoalId: undefined } : n)) };
      notes.forEach((n) => dirtied.push({ collection: "notes", id: n.id }));
    }
  }

  if (collection === "projects") {
    const tasks = next.tasks.filter((t) => t.projectId === deletedId);
    if (tasks.length) {
      next = { ...next, tasks: next.tasks.map((t) => (t.projectId === deletedId ? { ...t, projectId: undefined } : t)) };
      tasks.forEach((t) => dirtied.push({ collection: "tasks", id: t.id }));
    }
    const notes = next.notes.filter((n) => n.linkedProjectId === deletedId);
    if (notes.length) {
      next = { ...next, notes: next.notes.map((n) => (n.linkedProjectId === deletedId ? { ...n, linkedProjectId: undefined } : n)) };
      notes.forEach((n) => dirtied.push({ collection: "notes", id: n.id }));
    }
  }

  if (collection === "tasks") {
    // Time-block: sự kiện lịch trỏ vào task vừa xoá
    const events = next.events.filter((e) => e.linkedTaskId === deletedId);
    if (events.length) {
      next = { ...next, events: next.events.map((e) => (e.linkedTaskId === deletedId ? { ...e, linkedTaskId: undefined } : e)) };
      events.forEach((e) => dirtied.push({ collection: "events", id: e.id }));
    }
    // MIT trong kế hoạch ngày
    const plans = next.dailyPlans.filter((p) => p.mitTaskIds.includes(deletedId));
    if (plans.length) {
      next = {
        ...next,
        dailyPlans: next.dailyPlans.map((p) =>
          p.mitTaskIds.includes(deletedId) ? { ...p, mitTaskIds: p.mitTaskIds.filter((id) => id !== deletedId) } : p,
        ),
      };
      plans.forEach((p) => dirtied.push({ collection: "dailyPlans", id: p.id }));
    }
    // Series parent bị xoá: dừng chuỗi — instance tương lai chưa xong xoá luôn,
    // instance quá khứ/đã xong giữ lại làm task thường (gỡ link cha chết).
    const instances = next.tasks.filter((t) => t.recurrenceParentId === deletedId);
    if (instances.length) {
      const keepIds = new Set(
        instances.filter((t) => t.status === "done" || t.dueDate <= today).map((t) => t.id),
      );
      instances.forEach((t) => {
        if (keepIds.has(t.id)) dirtied.push({ collection: "tasks", id: t.id });
        else removeTaskIds.push(t.id);
      });
      next = {
        ...next,
        tasks: next.tasks.map((t) =>
          t.recurrenceParentId === deletedId && keepIds.has(t.id)
            ? { ...t, recurrenceParentId: undefined, recurrenceKey: undefined }
            : t,
        ),
      };
    }
  }

  if (collection === "accounts") {
    const txns = next.transactions.filter((t) => t.accountId === deletedId || t.toAccountId === deletedId);
    if (txns.length) {
      next = {
        ...next,
        transactions: next.transactions.map((t) =>
          t.accountId === deletedId || t.toAccountId === deletedId
            ? {
                ...t,
                accountId: t.accountId === deletedId ? undefined : t.accountId,
                toAccountId: t.toAccountId === deletedId ? undefined : t.toAccountId,
              }
            : t,
        ),
      };
      txns.forEach((t) => dirtied.push({ collection: "transactions", id: t.id }));
    }
    const rules = next.recurring.filter((r) => r.accountId === deletedId);
    if (rules.length) {
      next = { ...next, recurring: next.recurring.map((r) => (r.accountId === deletedId ? { ...r, accountId: undefined } : r)) };
      rules.forEach((r) => dirtied.push({ collection: "recurring", id: r.id }));
    }
  }

  return { state: next, dirtied, removeTaskIds };
}
